/**
 * サンプル管理データの正本: ユーザー PC 上の sample-manager.zip
 * - ディスクにデータを永続化しない（セッション中のメモリのみ）
 * - Chrome / Edge: File System Access API で選択ファイルへ直接上書き（保存ダイアログなし）
 * - ハンドルなし: メモリ上で編集し、ユーザーが明示したときだけダウンロード（都度DL・保存ダイアログなし）
 */
import { emptySampleSnapshot, getSampleLocalSnapshotOrNull, resetSampleLocalSession, setSampleSnapshotWrittenHook, type SampleLocalSnapshot } from './sampleLocalDb';
import { sampleLocalStore } from './sampleLocalStore';
import {
  clearStoredSampleZipHandle,
  loadStoredSampleZipHandle,
  saveStoredSampleZipHandle,
} from './sampleZipHandleStore';
import {
  clearHibernatedSampleZipSession,
  loadHibernatedSampleZipSession,
  peekHibernatedSampleZipResume,
  saveHibernatedSampleZipSession,
} from './sampleZipSessionStore';
import {
  exportSampleZip,
  importSampleZip,
  parseSampleZipBuffer,
} from './sampleZipBundle';
import { extractLocalFilePathHint } from '../../../utils/localZip/localFilePathHint';
import {
  getLatestLocalZipRecent,
  migrateLegacyLastZipFileName,
  rememberLocalZipRecent,
} from '../../../utils/localZip/localZipRecentStore';

const ZIP_FILENAME = 'sample-manager.zip';
const LAST_ZIP_NAME_KEY = 'sample-manager.lastZipFileName';
const SYNC_DEBOUNCE_MS = 800;

/** 再読み込み後の「開き直し」案内用（ファイル本体は保持しない） */
export function getLastSampleZipFileName(): string | null {
  const recent =
    getLatestLocalZipRecent('sample-manager') ||
    migrateLegacyLastZipFileName('sample-manager', LAST_ZIP_NAME_KEY);
  if (recent?.name) return recent.name;
  try {
    const name = localStorage.getItem(LAST_ZIP_NAME_KEY);
    return name && name.trim() ? name.trim() : null;
  } catch {
    return null;
  }
}

function rememberLastZipFileName(fileName: string, pathHint?: string | null): void {
  rememberLocalZipRecent('sample-manager', { name: fileName, path: pathHint ?? null });
  try {
    localStorage.setItem(LAST_ZIP_NAME_KEY, fileName);
  } catch {
    // private mode 等では無視
  }
}

const ZIP_ACCEPT = {
  types: [{ description: 'Sample Manager data', accept: { 'application/zip': ['.zip'] } }],
};

type HandleWithPermission = FileSystemFileHandle & {
  queryPermission?(opts: { mode: 'readwrite' }): Promise<PermissionState>;
  requestPermission?(opts: { mode: 'readwrite' }): Promise<PermissionState>;
};

/** file-handle: 選択ファイルへ静かに上書き / manual-download: 明示DLまでメモリのみ */
export type SampleZipAutoSaveMode = 'file-handle' | 'manual-download';

export type SampleZipConnection = {
  fileName: string;
  autoSaveMode: SampleZipAutoSaveMode;
  lastSavedAt: string | null;
  /** ハンドルなしで未ダウンロードの変更がある */
  dirty: boolean;
  /** スナップショット差し替えのたびに増える（同名再接続でも RQ invalidate） */
  dataEpoch: number;
};

type ZipFileFingerprint = {
  lastModified: number;
  size: number;
};

export type SampleZipExternalChangeResult = 'unchanged' | 'reloaded' | 'no_handle' | 'error';

let activeHandle: FileSystemFileHandle | null = null;
let activeFileName: string | null = null;
let connected = false;
let autoSaveMode: SampleZipAutoSaveMode = 'manual-download';
let lastSavedAt: string | null = null;
let sessionDirty = false;
let syncTimer: ReturnType<typeof setTimeout> | null = null;
let syncInFlight: Promise<void> | null = null;
let pendingSync = false;
let lastKnownFingerprint: ZipFileFingerprint | null = null;
let dataEpoch = 0;
let hibernateTimer: ReturnType<typeof setTimeout> | null = null;
/** 自動書き戻しが manual-download に落ちたとき、UI が一度だけ通知する */
let autosaveDowngradeNoticePending = false;

type ConnectionListener = () => void;
const connectionListeners = new Set<ConnectionListener>();

function notifyConnectionChange(): void {
  for (const listener of connectionListeners) {
    listener();
  }
}

export function subscribeSampleZipConnection(listener: ConnectionListener): () => void {
  connectionListeners.add(listener);
  return () => connectionListeners.delete(listener);
}

/** Shell が購読時に消費。true ならユーザーへ手動 DL 案内を出す */
export function consumeSampleZipAutosaveDowngradeNotice(): boolean {
  const v = autosaveDowngradeNoticePending;
  autosaveDowngradeNoticePending = false;
  return v;
}

function markAutosaveDowngraded(): void {
  if (autoSaveMode === 'manual-download') return;
  autoSaveMode = 'manual-download';
  sessionDirty = true;
  autosaveDowngradeNoticePending = true;
}

function isFsaaSupported(): boolean {
  return typeof window !== 'undefined' && 'showOpenFilePicker' in window;
}

async function ensureWritePermission(handle: FileSystemFileHandle): Promise<boolean> {
  const h = handle as HandleWithPermission;
  if (typeof h.queryPermission !== 'function') return true;
  let state = await h.queryPermission({ mode: 'readwrite' });
  if (state === 'granted') return true;
  if (typeof h.requestPermission !== 'function') return false;
  state = await h.requestPermission({ mode: 'readwrite' });
  return state === 'granted';
}

async function writeBlobToHandle(handle: FileSystemFileHandle, blob: Blob): Promise<void> {
  const writable = await handle.createWritable();
  await writable.write(blob);
  await writable.close();
}

function clearPendingZipSync(): void {
  if (syncTimer) {
    clearTimeout(syncTimer);
    syncTimer = null;
  }
  pendingSync = false;
  if (hibernateTimer) {
    clearTimeout(hibernateTimer);
    hibernateTimer = null;
  }
}

async function persistHibernateNow(): Promise<void> {
  const snap = getSampleLocalSnapshotOrNull();
  if (!snap || !activeFileName) return;
  await saveHibernatedSampleZipSession({
    fileName: activeFileName,
    dirty: sessionDirty,
    autoSaveMode,
    snapshot: snap,
  });
}

function scheduleHibernatePersist(): void {
  if (!connected) return;
  if (hibernateTimer) clearTimeout(hibernateTimer);
  hibernateTimer = setTimeout(() => {
    hibernateTimer = null;
    void persistHibernateNow();
  }, 400);
}

function setConnection(
  fileName: string,
  handle: FileSystemFileHandle | null,
  mode: SampleZipAutoSaveMode,
  pathHint?: string | null
): void {
  clearPendingZipSync();
  activeHandle = handle;
  activeFileName = fileName;
  connected = true;
  autoSaveMode = mode;
  sessionDirty = false;
  lastSavedAt = mode === 'file-handle' ? new Date().toISOString() : null;
  dataEpoch += 1;
  rememberLastZipFileName(fileName, pathHint);
  if (handle) {
    void saveStoredSampleZipHandle(handle);
  }
  notifyConnectionChange();
}

function disconnectInternal(): void {
  clearPendingZipSync();
  activeHandle = null;
  activeFileName = null;
  connected = false;
  autoSaveMode = 'manual-download';
  lastSavedAt = null;
  sessionDirty = false;
  lastKnownFingerprint = null;
  resetSampleLocalSession();
  notifyConnectionChange();
}

function fingerprintOf(file: File): ZipFileFingerprint {
  return { lastModified: file.lastModified, size: file.size };
}

function fingerprintsEqual(a: ZipFileFingerprint | null, b: ZipFileFingerprint | null): boolean {
  return a != null && b != null && a.lastModified === b.lastModified && a.size === b.size;
}

async function refreshFingerprintFromHandle(): Promise<ZipFileFingerprint | null> {
  if (!activeHandle) return null;
  const file = await activeHandle.getFile();
  return fingerprintOf(file);
}

export function isSampleZipConnected(): boolean {
  return connected;
}

export function getSampleZipConnection(): SampleZipConnection | null {
  if (!connected || !activeFileName) return null;
  return {
    fileName: activeFileName,
    autoSaveMode,
    lastSavedAt,
    dirty: sessionDirty,
    dataEpoch,
  };
}

export function isSampleFsaaSupported(): boolean {
  return isFsaaSupported();
}

export type SampleZipRestoreResult = 'restored' | 'needs_gesture' | 'none' | 'error';

/** 再開可能な前回セッションの有無（FSAA ハンドルまたはハイバネート） */
export async function peekStoredSampleZipResume(): Promise<{ fileName: string } | null> {
  if (isFsaaSupported()) {
    const handle = await loadStoredSampleZipHandle();
    if (handle) {
      const name = (handle.name || getLastSampleZipFileName() || ZIP_FILENAME).trim();
      return { fileName: name || ZIP_FILENAME };
    }
  }
  const hib = await peekHibernatedSampleZipResume();
  if (hib) return { fileName: hib.fileName };
  return null;
}

/**
 * 権限が既に granted なら無操作で再開。
 * prompt / denied のときは needs_gesture（ユーザー操作で resumeLastSampleZip）。
 * ハンドルが無い／使えないときはハイバネートからメモリ再開。
 */
export async function tryRestoreSampleZipSession(): Promise<SampleZipRestoreResult> {
  if (connected) return 'restored';

  if (isFsaaSupported()) {
    const handle = await loadStoredSampleZipHandle();
    if (handle) {
      const h = handle as HandleWithPermission;
      try {
        const state =
          typeof h.queryPermission === 'function'
            ? await h.queryPermission({ mode: 'readwrite' })
            : 'granted';
        if (state !== 'granted') {
          // ハイバネートがあれば先にメモリ再開（ワンクリックで続き可能）
          if (await restoreFromHibernateIfAny()) return 'restored';
          return 'needs_gesture';
        }
        await connectWithHandle(handle);
        return 'restored';
      } catch {
        await clearStoredSampleZipHandle();
      }
    }
  }

  if (await restoreFromHibernateIfAny()) return 'restored';
  return 'none';
}

async function restoreFromHibernateIfAny(): Promise<boolean> {
  const hib = await loadHibernatedSampleZipSession();
  if (!hib) return false;
  await sampleLocalStore.replaceSnapshot(hib.snapshot);
  const recent = getLatestLocalZipRecent('sample-manager');
  const pathHint = recent && recent.name === hib.fileName ? recent.path : null;
  setConnection(hib.fileName, null, 'manual-download', pathHint);
  sessionDirty = hib.dirty;
  notifyConnectionChange();
  return true;
}

/** 前回 Zip をワンクリック再開（ユーザー操作内で permission を要求） */
export async function resumeLastSampleZip(): Promise<void> {
  const handle = await loadStoredSampleZipHandle();
  if (handle) {
    try {
      await connectWithHandle(handle);
      return;
    } catch (e) {
      await clearStoredSampleZipHandle();
      // ハンドル失敗時はハイバネートへフォールバック
      if (await restoreFromHibernateIfAny()) return;
      throw e;
    }
  }
  if (await restoreFromHibernateIfAny()) return;
  throw new Error('zip_no_stored_handle');
}

async function loadSnapshotFromHandle(handle: FileSystemFileHandle): Promise<File> {
  const file = await handle.getFile();
  const snapshot = await parseSampleZipBuffer(await file.arrayBuffer());
  await sampleLocalStore.replaceSnapshot(snapshot);
  lastKnownFingerprint = fingerprintOf(file);
  return file;
}

async function connectWithHandle(handle: FileSystemFileHandle, initialSnap?: SampleLocalSnapshot): Promise<void> {
  const granted = await ensureWritePermission(handle);
  let pathHint: string | null = null;
  if (initialSnap) {
    await sampleLocalStore.replaceSnapshot(initialSnap);
    lastKnownFingerprint = await refreshFingerprintFromHandle();
    try {
      pathHint = extractLocalFilePathHint(await handle.getFile());
    } catch {
      pathHint = null;
    }
  } else {
    const file = await loadSnapshotFromHandle(handle);
    pathHint = extractLocalFilePathHint(file);
  }
  setConnection(handle.name, handle, granted ? 'file-handle' : 'manual-download', pathHint);
}

async function connectFromFile(file: File, handle: FileSystemFileHandle | null): Promise<void> {
  await importSampleZip(file);
  lastKnownFingerprint = fingerprintOf(file);
  const pathHint = extractLocalFilePathHint(file);
  if (handle) {
    const granted = await ensureWritePermission(handle);
    setConnection(handle.name, handle, granted ? 'file-handle' : 'manual-download', pathHint);
  } else {
    setConnection(file.name, null, 'manual-download', pathHint);
  }
}

export async function checkSampleZipExternalChange(): Promise<SampleZipExternalChangeResult> {
  if (!connected || !activeHandle || autoSaveMode !== 'file-handle') {
    return 'no_handle';
  }
  try {
    if (syncInFlight) await syncInFlight;
    if (pendingSync) await flushSampleZipSync();

    const fp = await refreshFingerprintFromHandle();
    if (fingerprintsEqual(fp, lastKnownFingerprint)) {
      return 'unchanged';
    }
    await loadSnapshotFromHandle(activeHandle);
    sessionDirty = false;
    dataEpoch += 1;
    notifyConnectionChange();
    return 'reloaded';
  } catch {
    disconnectInternal();
    return 'error';
  }
}

export async function openExistingSampleZip(): Promise<void> {
  if (isFsaaSupported()) {
    try {
      const [handle] = await (
        window as unknown as Window & {
          showOpenFilePicker: (o?: unknown) => Promise<FileSystemFileHandle[]>;
        }
      ).showOpenFilePicker({ ...ZIP_ACCEPT, multiple: false });
      await connectWithHandle(handle);
      return;
    } catch (e) {
      if ((e as Error).name === 'AbortError') throw new Error('zip_pick_cancelled');
      const msg = e instanceof Error ? e.message : '';
      // confirm() 等でユーザー操作が切れると SecurityError / gesture 文言になる
      if (
        (e as Error).name === 'SecurityError' ||
        /user gesture/i.test(msg) ||
        /show a file picker/i.test(msg)
      ) {
        throw new Error('zip_pick_requires_gesture');
      }
      throw e;
    }
  }
  const file = await pickZipFileViaInput();
  if (!file) throw new Error('zip_pick_cancelled');
  await connectFromFile(file, null);
}

/**
 * 新規セッションをすぐ開始（保存先ダイアログ・自動ダウンロードなし）。
 * ディスクへ残すときは「Zip をダウンロード」を押す。
 */
export async function createNewSampleZip(): Promise<void> {
  await clearStoredSampleZipHandle();
  await clearHibernatedSampleZipSession();
  await sampleLocalStore.replaceSnapshot(emptySampleSnapshot());
  setConnection(ZIP_FILENAME, null, 'manual-download');
  scheduleHibernatePersist();
}

/**
 * ファイルピッカーなしで Zip 相当セッションを開始（自動テスト・ブラウザ QA 用）。
 * 保存は manual-download（明示ダウンロードまでメモリのみ）。
 */
export async function connectSampleZipMemorySession(
  snapshot: SampleLocalSnapshot = emptySampleSnapshot(),
  fileName = ZIP_FILENAME
): Promise<void> {
  await sampleLocalStore.replaceSnapshot(snapshot);
  setConnection(fileName, null, 'manual-download');
  scheduleHibernatePersist();
}

/**
 * DB 正本へ切替える前に作業コピーを退避して切断（再開可能）。
 * FSAA ハンドルは IndexedDB に残す。
 */
export async function pauseSampleZipSession(): Promise<void> {
  if (!connected) return;
  try {
    await flushSampleZipSync();
  } catch {
    /* 退避優先 */
  }
  await persistHibernateNow();
  disconnectInternal();
}

/**
 * 接続を解除。既定はハイバネートして再開可能にする。
 * discard: true でハンドル・ハイバネートも破棄。
 */
export async function disconnectSampleZipSession(opts?: { discard?: boolean }): Promise<void> {
  if (connected) {
    if (!opts?.discard) {
      try {
        await flushSampleZipSync();
      } catch {
        /* ignore */
      }
      await persistHibernateNow();
    }
    disconnectInternal();
  }
  if (opts?.discard) {
    void clearHibernatedSampleZipSession();
    void clearStoredSampleZipHandle();
  }
}

function scheduleSampleZipSync(): void {
  if (!connected) return;
  scheduleHibernatePersist();
  // 手動DL: 書き戻し不要。未DL バッジを即出す（debounce 待ちは誤解を招く）
  if (autoSaveMode === 'manual-download') {
    if (!sessionDirty) {
      sessionDirty = true;
      notifyConnectionChange();
    }
    return;
  }
  pendingSync = true;
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(() => {
    syncTimer = null;
    void flushSampleZipSync();
  }, SYNC_DEBOUNCE_MS);
}

async function writeZipToFileHandle(blob: Blob): Promise<boolean> {
  if (autoSaveMode !== 'file-handle' || !activeHandle) return false;
  try {
    const ok = await ensureWritePermission(activeHandle);
    if (!ok) {
      markAutosaveDowngraded();
      notifyConnectionChange();
      return false;
    }
    await writeBlobToHandle(activeHandle, blob);
    sessionDirty = false;
    return true;
  } catch {
    markAutosaveDowngraded();
    notifyConnectionChange();
    return false;
  }
}

export async function flushSampleZipSync(): Promise<void> {
  if (!connected) return;
  if (syncInFlight) {
    await syncInFlight;
    if (pendingSync) return flushSampleZipSync();
    return;
  }

  pendingSync = false;
  syncInFlight = (async () => {
    if (autoSaveMode === 'file-handle' && activeHandle) {
      const blob = await exportSampleZip();
      const wrote = await writeZipToFileHandle(blob);
      if (wrote) {
        lastSavedAt = new Date().toISOString();
        lastKnownFingerprint = await refreshFingerprintFromHandle();
      }
      notifyConnectionChange();
    }
    // manual-download: flush は no-op。dirty は scheduleSampleZipSync のみが立てる
    // （ルート切替の unmount flush で偽の「未保存」にしない）
  })();

  try {
    await syncInFlight;
  } finally {
    syncInFlight = null;
  }
}

/** 明示ダウンロードのみ。保存先ダイアログは出さない（既定のダウンロードフォルダへ）。 */
export async function downloadCurrentSampleZip(): Promise<void> {
  if (!connected) return;
  if (syncTimer) {
    clearTimeout(syncTimer);
    syncTimer = null;
  }
  pendingSync = false;
  if (syncInFlight) await syncInFlight;
  const blob = await exportSampleZip();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = activeFileName ?? ZIP_FILENAME;
  a.click();
  URL.revokeObjectURL(url);
  sessionDirty = false;
  lastSavedAt = new Date().toISOString();
  notifyConnectionChange();
}

/** input[type=file] はキャンセル時 onchange が来ないことがある → focus 復帰で null 解決 */
function pickZipFileViaInput(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.zip,application/zip';
    let settled = false;
    const finish = (file: File | null) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('focus', onWindowFocus);
      resolve(file);
    };
    const onWindowFocus = () => {
      window.setTimeout(() => finish(input.files?.[0] ?? null), 400);
    };
    input.onchange = () => finish(input.files?.[0] ?? null);
    input.addEventListener('cancel', () => finish(null));
    window.addEventListener('focus', onWindowFocus);
    input.click();
  });
}

function onPageHideFlush(): void {
  if (!connected) return;
  // 作業コピーを必ず退避（再読込後の再開用）
  void persistHibernateNow();
  // ハンドルありのときだけ静かに Zip へ書き戻す
  if (autoSaveMode !== 'file-handle') return;
  if (!pendingSync && !syncInFlight) return;
  if (syncTimer) {
    clearTimeout(syncTimer);
    syncTimer = null;
  }
  void flushSampleZipSync();
}

function onBeforeUnload(e: BeforeUnloadEvent): void {
  if (!connected || autoSaveMode !== 'manual-download' || !sessionDirty) return;
  e.preventDefault();
  e.returnValue = '';
}

function onVisibilityChange(): void {
  if (document.visibilityState === 'hidden') {
    onPageHideFlush();
    return;
  }
  if (document.visibilityState === 'visible' && connected) {
    void checkSampleZipExternalChange().then((result) => {
      if (result === 'reloaded' || result === 'error') {
        notifyConnectionChange();
      }
    });
  }
}

export function initSampleZipAutoSync(): void {
  setSampleSnapshotWrittenHook(() => scheduleSampleZipSync());
  window.addEventListener('pagehide', onPageHideFlush);
  window.addEventListener('beforeunload', onBeforeUnload);
  document.addEventListener('visibilitychange', onVisibilityChange);
}

export function teardownSampleZipAutoSync(): void {
  setSampleSnapshotWrittenHook(null);
  window.removeEventListener('pagehide', onPageHideFlush);
  window.removeEventListener('beforeunload', onBeforeUnload);
  document.removeEventListener('visibilitychange', onVisibilityChange);
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = null;
  pendingSync = false;
  if (hibernateTimer) {
    clearTimeout(hibernateTimer);
    hibernateTimer = null;
  }
}

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (
    window as unknown as {
      __connectSampleZipMemorySession?: typeof connectSampleZipMemorySession;
    }
  ).__connectSampleZipMemorySession = connectSampleZipMemorySession;
}
